import { useCallback, useEffect, useMemo, useState } from "react";
import { generateClient } from "aws-amplify/api";
import { get } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import { listSessionAnalyses, listCodingAnalyses } from "../graphql/queries";
import { listLomnAnalyses } from "../graphql/lomnAnalysis";
import { useAuth } from "../AuthProvider";
import { attachCostEstimates, formatCurrency } from "./developerDashboardCosting";
import "./DeveloperDashboard.css";

const client = generateClient();

const SESSION_PAGE_LIMIT = 250;
const CODING_PAGE_LIMIT = 250;
const LOMN_PAGE_LIMIT = 250;

const LOMN_TEMPLATE_LABELS = {
  PAYER_APPEAL: "Payer Appeal",
  DME_AUTHORIZATION: "DME Authorization",
  IMAGING_JUSTIFICATION: "Imaging Justification",
  IDR_FINAL_OFFER: "IDR Final Offer",
  MEDICAL_RECORD_REQUEST: "Medical Record Request",
};

function toInputDate(date) {
  return date.toISOString().slice(0, 10);
}

function getCurrentMonthStartInput() {
  const now = new Date();
  return toInputDate(new Date(now.getFullYear(), now.getMonth(), 1));
}

function isInDateRange(isoDateTime, startDate, endDate) {
  if (!isoDateTime) {
    return false;
  }

  const valueTs = new Date(isoDateTime).getTime();
  if (!Number.isFinite(valueTs)) {
    return false;
  }

  if (startDate) {
    const startTs = new Date(`${startDate}T00:00:00.000Z`).getTime();
    if (valueTs < startTs) {
      return false;
    }
  }

  if (endDate) {
    const endTs = new Date(`${endDate}T23:59:59.999Z`).getTime();
    if (valueTs > endTs) {
      return false;
    }
  }

  return true;
}

async function listAllItems(query, listKey, limit) {
  let nextToken = null;
  const allItems = [];

  do {
    let response;
    try {
      response = await client.graphql({
        query,
        variables: {
          limit,
          nextToken,
        },
      });
    } catch (err) {
      // AppSync can return partial data with GraphQL errors when one item in a list is malformed.
      // Keep the dashboard usable by continuing with valid items from the partial payload.
      const partial = err?.data?.[listKey];
      if (!partial) {
        throw err;
      }
      response = { data: { [listKey]: partial } };
      console.warn(`Partial GraphQL data returned for ${listKey}`, {
        errorCount: Array.isArray(err?.errors) ? err.errors.length : 0,
      });
    }

    const payload = response?.data?.[listKey];
    const items = payload?.items || [];
    allItems.push(...items.filter(Boolean));
    nextToken = payload?.nextToken || null;
  } while (nextToken);

  return allItems;
}

function byNewest(a, b) {
  return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
}

function isCompletedStatus(status) {
  return String(status || "").toUpperCase() === "COMPLETED";
}

function isFailedStatus(status) {
  return String(status || "").toUpperCase() === "FAILED";
}

function isInProgressStatus(status) {
  const normalized = String(status || "").toUpperCase();
  return normalized === "UPLOADING" || normalized === "QUEUED" || normalized === "PROCESSING";
}

function getLomnTemplateLabel(templateType) {
  const key = String(templateType || "").toUpperCase();
  if (!key) return "Unknown";
  return LOMN_TEMPLATE_LABELS[key] || key;
}

function buildUserRows(sessionItems, codingItems, lomnItems) {
  const map = new Map();

  const ensure = (userId) => {
    const key = userId || "unknown";
    if (!map.has(key)) {
      map.set(key, {
        userId: key,
        transcribedCount: 0,
        codingSubmittedCount: 0,
        codingCompletedCount: 0,
        lomnSubmittedCount: 0,
        lomnCompletedCount: 0,
        lomnFailedCount: 0,
        lastActivityAt: null,
      });
    }
    return map.get(key);
  };

  sessionItems.forEach((item) => {
    const row = ensure(item?.userId);
    row.transcribedCount += 1;

    const createdAt = item?.createdAt || null;
    if (createdAt && (!row.lastActivityAt || createdAt > row.lastActivityAt)) {
      row.lastActivityAt = createdAt;
    }
  });

  codingItems.forEach((item) => {
    const row = ensure(item?.userId);
    row.codingSubmittedCount += 1;

    if (isCompletedStatus(item?.processingStatus)) {
      row.codingCompletedCount += 1;
    }

    const createdAt = item?.createdAt || null;
    if (createdAt && (!row.lastActivityAt || createdAt > row.lastActivityAt)) {
      row.lastActivityAt = createdAt;
    }
  });

  lomnItems.forEach((item) => {
    const row = ensure(item?.userId);
    row.lomnSubmittedCount += 1;

    if (isCompletedStatus(item?.processingStatus)) {
      row.lomnCompletedCount += 1;
    }

    if (isFailedStatus(item?.processingStatus)) {
      row.lomnFailedCount += 1;
    }

    const createdAt = item?.createdAt || null;
    if (createdAt && (!row.lastActivityAt || createdAt > row.lastActivityAt)) {
      row.lastActivityAt = createdAt;
    }
  });

  return Array.from(map.values()).sort((a, b) => {
    const aLast = new Date(a.lastActivityAt || 0).getTime();
    const bLast = new Date(b.lastActivityAt || 0).getTime();
    return bLast - aLast;
  });
}

export default function DeveloperDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionItems, setSessionItems] = useState([]);
  const [codingItems, setCodingItems] = useState([]);
  const [lomnItems, setLomnItems] = useState([]);
  const [directoryUsers, setDirectoryUsers] = useState([]);
  const [groupCounts, setGroupCounts] = useState({});
  const [rangeStart, setRangeStart] = useState(() => getCurrentMonthStartInput());
  const [rangeEnd, setRangeEnd] = useState(() => toInputDate(new Date()));

  const codingApiName = process.env.REACT_APP_CODING_API_NAME || "accountRequestsAPI";
  const developerMetricsPath =
    process.env.REACT_APP_DEVELOPER_METRICS_PATH || "/developer/metrics";

  const loadDeveloperDirectory = useCallback(async () => {
    const authSession = await fetchAuthSession();
    const accessToken = authSession?.tokens?.accessToken?.toString();

    if (!accessToken) {
      throw new Error("No access token available for developer metrics.");
    }

    const response = await get({
      apiName: codingApiName,
      path: developerMetricsPath,
      options: {
        queryParams: {
          accessToken,
        },
      },
    }).response;

    return response.body.json();
  }, [codingApiName, developerMetricsPath]);

  const loadMetrics = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [allSessions, allCoding, allLomn, directoryPayload] = await Promise.all([
        listAllItems(listSessionAnalyses, "listSessionAnalyses", SESSION_PAGE_LIMIT),
        listAllItems(listCodingAnalyses, "listCodingAnalyses", CODING_PAGE_LIMIT),
        listAllItems(listLomnAnalyses, "listLOMNAnalyses", LOMN_PAGE_LIMIT),
        loadDeveloperDirectory(),
      ]);

      const validCoding = allCoding.filter((item) => {
        const userId = String(item?.userId || "").trim();
        return userId.length > 0;
      });

      const validLomn = allLomn.filter((item) => {
        const userId = String(item?.userId || "").trim();
        return userId.length > 0;
      });

      setSessionItems(allSessions.sort(byNewest));
      setCodingItems(validCoding.sort(byNewest));
      setLomnItems(validLomn.sort(byNewest));
      setDirectoryUsers(Array.isArray(directoryPayload?.users) ? directoryPayload.users : []);
      setGroupCounts(
        directoryPayload?.groups && typeof directoryPayload.groups === "object"
          ? directoryPayload.groups
          : {}
      );
    } catch (err) {
      console.error("Failed to load developer metrics", err);
      setError(
        "Could not load dashboard data. Confirm your developer group has read access and /developer/metrics is deployed."
      );
    } finally {
      setLoading(false);
    }
  }, [loadDeveloperDirectory]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const filteredSessionItems = useMemo(() => {
    return sessionItems.filter((item) => isInDateRange(item?.createdAt, rangeStart, rangeEnd));
  }, [sessionItems, rangeStart, rangeEnd]);

  const filteredCodingItems = useMemo(() => {
    return codingItems.filter((item) => isInDateRange(item?.createdAt, rangeStart, rangeEnd));
  }, [codingItems, rangeStart, rangeEnd]);

  const filteredLomnItems = useMemo(() => {
    return lomnItems.filter((item) => isInDateRange(item?.createdAt, rangeStart, rangeEnd));
  }, [lomnItems, rangeStart, rangeEnd]);

  const metrics = useMemo(() => {
    const userRows = attachCostEstimates(
      buildUserRows(filteredSessionItems, filteredCodingItems, filteredLomnItems)
    );
    const uniqueUsers = userRows.length;
    const totalTranscribed = filteredSessionItems.length;
    const totalCodingSubmitted = filteredCodingItems.length;
    const totalCodingCompleted = filteredCodingItems.filter((item) => isCompletedStatus(item?.processingStatus)).length;
    const totalLomnSubmitted = filteredLomnItems.length;
    const totalLomnCompleted = filteredLomnItems.filter((item) => isCompletedStatus(item?.processingStatus)).length;
    const totalLomnInProgress = filteredLomnItems.filter((item) => isInProgressStatus(item?.processingStatus)).length;
    const totalLomnFailed = filteredLomnItems.filter((item) => isFailedStatus(item?.processingStatus)).length;

    return {
      uniqueUsers,
      totalTranscribed,
      totalCodingSubmitted,
      totalCodingCompleted,
      totalLomnSubmitted,
      totalLomnCompleted,
      totalLomnInProgress,
      totalLomnFailed,
      userRows,
    };
  }, [filteredSessionItems, filteredCodingItems, filteredLomnItems]);

  const groupsByUser = useMemo(() => {
    const map = new Map();
    directoryUsers.forEach((userItem) => {
      map.set(userItem?.username, Array.isArray(userItem?.groups) ? userItem.groups : []);
    });
    return map;
  }, [directoryUsers]);

  const groupSummary = useMemo(() => {
    return Object.entries(groupCounts || {}).sort((a, b) => String(a[0]).localeCompare(String(b[0])));
  }, [groupCounts]);

  const lomnTemplateSummary = useMemo(() => {
    const byTemplate = new Map();

    filteredLomnItems.forEach((item) => {
      const templateKey = String(item?.templateType || "UNKNOWN").toUpperCase() || "UNKNOWN";
      if (!byTemplate.has(templateKey)) {
        byTemplate.set(templateKey, {
          templateKey,
          templateLabel: getLomnTemplateLabel(templateKey),
          submittedCount: 0,
          completedCount: 0,
          inProgressCount: 0,
          failedCount: 0,
        });
      }

      const row = byTemplate.get(templateKey);
      row.submittedCount += 1;

      if (isCompletedStatus(item?.processingStatus)) {
        row.completedCount += 1;
      } else if (isInProgressStatus(item?.processingStatus)) {
        row.inProgressCount += 1;
      } else if (isFailedStatus(item?.processingStatus)) {
        row.failedCount += 1;
      }
    });

    return Array.from(byTemplate.values())
      .map((row) => ({
        ...row,
        completionRate:
          row.submittedCount > 0
            ? Math.round((row.completedCount / row.submittedCount) * 100)
            : 0,
      }))
      .sort((a, b) => b.submittedCount - a.submittedCount || a.templateLabel.localeCompare(b.templateLabel));
  }, [filteredLomnItems]);

  const recentSessions = useMemo(() => filteredSessionItems.slice(0, 10), [filteredSessionItems]);
  const recentCoding = useMemo(() => filteredCodingItems.slice(0, 10), [filteredCodingItems]);
  const recentLomn = useMemo(() => filteredLomnItems.slice(0, 10), [filteredLomnItems]);

  return (
    <div className="dev-dashboard">
      <div className="dev-dashboard-header">
        <div>
          <h2>Developer Dashboard</h2>
          <p>
            Monitoring users and note volume across transcription, coding analysis, and Letter of Medical Necessity requests.
          </p>
          <p className="dev-dashboard-meta">
            Signed in as {user?.username || "unknown"} | Cognito groups: {user?.groups?.join(", ") || "none"}
          </p>
        </div>
        <button className="button" onClick={loadMetrics} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh Metrics"}
        </button>
      </div>

      {error && <p className="dev-dashboard-error">{error}</p>}

      <section className="dev-table-card">
        <h3>Date Range</h3>
        <div className="dev-date-filters">
          <label>
            From
            <input
              type="date"
              value={rangeStart}
              onChange={(e) => setRangeStart(e.target.value)}
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={rangeEnd}
              onChange={(e) => setRangeEnd(e.target.value)}
            />
          </label>
          <button
            className="button"
            onClick={() => {
              setRangeStart(getCurrentMonthStartInput());
              setRangeEnd(toInputDate(new Date()));
            }}
          >
            This Month
          </button>
        </div>
        <p className="dev-dashboard-meta">
          Showing activity from {rangeStart || "the beginning"} to {rangeEnd || "today"}.
        </p>
        <p className="dev-dashboard-meta">
          Estimated costs use simple per-action rates for transcription, coding, and LOMN requests.
        </p>
      </section>

      <div className="dev-kpi-grid">
        <article className="dev-kpi-card">
          <h3>Active Users</h3>
          <strong>{metrics.uniqueUsers}</strong>
          <span>Unique users with transcription, coding, or LOMN activity</span>
        </article>
        <article className="dev-kpi-card">
          <h3>Directory Users</h3>
          <strong>{directoryUsers.length}</strong>
          <span>Total Cognito users visible to developers</span>
        </article>
        <article className="dev-kpi-card">
          <h3>Notes Transcribed</h3>
          <strong>{metrics.totalTranscribed}</strong>
          <span>Total SessionAnalysis notes</span>
        </article>
        <article className="dev-kpi-card">
          <h3>Coding Submitted</h3>
          <strong>{metrics.totalCodingSubmitted}</strong>
          <span>Total CodingAnalysis requests</span>
        </article>
        <article className="dev-kpi-card">
          <h3>Coding Completed</h3>
          <strong>{metrics.totalCodingCompleted}</strong>
          <span>Completed coding analyses</span>
        </article>
        <article className="dev-kpi-card">
          <h3>LOMN Submitted</h3>
          <strong>{metrics.totalLomnSubmitted}</strong>
          <span>Total LOMN requests</span>
        </article>
        <article className="dev-kpi-card">
          <h3>LOMN Completed</h3>
          <strong>{metrics.totalLomnCompleted}</strong>
          <span>Completed LOMN analyses</span>
        </article>
        <article className="dev-kpi-card">
          <h3>LOMN In Progress</h3>
          <strong>{metrics.totalLomnInProgress}</strong>
          <span>Uploading, queued, or processing</span>
        </article>
        <article className="dev-kpi-card">
          <h3>LOMN Failed</h3>
          <strong>{metrics.totalLomnFailed}</strong>
          <span>LOMN analyses with failed status</span>
        </article>
      </div>

      <section className="dev-table-card">
        <h3>Group Summary</h3>
        <div className="dev-group-summary">
          {groupSummary.map(([groupName, count]) => (
            <div key={groupName} className="dev-group-pill">
              <strong>{groupName}</strong>
              <span>{count} user(s)</span>
            </div>
          ))}
          {!groupSummary.length && <p>No groups found in directory snapshot.</p>}
        </div>
      </section>

      <section className="dev-table-card">
        <h3>LOMN Template Breakdown</h3>
        <div className="dev-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Template</th>
                <th>Submitted</th>
                <th>Completed</th>
                <th>In Progress</th>
                <th>Failed</th>
                <th>Completion Rate</th>
              </tr>
            </thead>
            <tbody>
              {lomnTemplateSummary.map((row) => (
                <tr key={row.templateKey}>
                  <td>{row.templateLabel}</td>
                  <td>{row.submittedCount}</td>
                  <td>{row.completedCount}</td>
                  <td>{row.inProgressCount}</td>
                  <td>{row.failedCount}</td>
                  <td>{row.completionRate}%</td>
                </tr>
              ))}
              {!lomnTemplateSummary.length && (
                <tr>
                  <td colSpan={6}>No LOMN requests in the selected date range.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="dev-table-card">
        <h3>Per-User Activity</h3>
        <div className="dev-table-wrap">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Groups</th>
                <th>Transcribed Notes</th>
                <th>Coding Submitted</th>
                <th>Coding Completed</th>
                <th>LOMN Submitted</th>
                <th>LOMN Completed</th>
                <th>LOMN Failed</th>
                <th>Estimated AWS</th>
                <th>Estimated OpenAI</th>
                <th>Estimated Total</th>
                <th>Last Activity</th>
              </tr>
            </thead>
            <tbody>
              {metrics.userRows.map((row) => (
                <tr key={row.userId}>
                  <td>{row.userId}</td>
                  <td>{(groupsByUser.get(row.userId) || []).join(", ") || "none"}</td>
                  <td>{row.transcribedCount}</td>
                  <td>{row.codingSubmittedCount}</td>
                  <td>{row.codingCompletedCount}</td>
                  <td>{row.lomnSubmittedCount}</td>
                  <td>{row.lomnCompletedCount}</td>
                  <td>{row.lomnFailedCount}</td>
                  <td>{formatCurrency(row.estimatedAwsCost)}</td>
                  <td>{formatCurrency(row.estimatedOpenAiCost)}</td>
                  <td>{formatCurrency(row.estimatedTotalCost)}</td>
                  <td>{row.lastActivityAt ? new Date(row.lastActivityAt).toLocaleString() : "n/a"}</td>
                </tr>
              ))}
              {!metrics.userRows.length && (
                <tr>
                  <td colSpan={12}>No activity available yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="dev-activity-grid">
        <section className="dev-table-card">
          <h3>Recent Transcriptions</h3>
          <ul className="dev-activity-list">
            {recentSessions.map((item) => (
              <li key={item.id}>
                <strong>{item.userId}</strong>
                <span>{new Date(item.createdAt).toLocaleString()}</span>
              </li>
            ))}
            {!recentSessions.length && <li>No transcription records yet.</li>}
          </ul>
        </section>

        <section className="dev-table-card">
          <h3>Recent Coding Requests</h3>
          <ul className="dev-activity-list">
            {recentCoding.map((item) => (
              <li key={item.id}>
                <div>
                  <strong>{item.userId}</strong>
                  <span>{item.caseName || "Untitled case"}</span>
                </div>
                <span>
                  {item.processingStatus || "UNKNOWN"} | {new Date(item.createdAt).toLocaleString()}
                </span>
              </li>
            ))}
            {!recentCoding.length && <li>No coding requests yet.</li>}
          </ul>
        </section>

        <section className="dev-table-card">
          <h3>Recent LOMN Requests</h3>
          <ul className="dev-activity-list">
            {recentLomn.map((item) => (
              <li key={item.id}>
                <div>
                  <strong>{item.userId}</strong>
                  <span>{item.caseName || "Untitled case"}</span>
                </div>
                <span>
                  {item.processingStatus || "UNKNOWN"} | {new Date(item.createdAt).toLocaleString()}
                </span>
              </li>
            ))}
            {!recentLomn.length && <li>No LOMN requests yet.</li>}
          </ul>
        </section>
      </div>
    </div>
  );
}
